'use client'

import { useMemo, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  CalculatorIcon,
  CalendarIcon,
  DownloadIcon,
  EyeIcon,
  GlobeIcon,
  HashIcon,
  ListIcon,
  MoreHorizontalIcon,
  PackageIcon,
  RefreshCwIcon,
  SquareMenuIcon,
  SquarePlusIcon,
  Trash2Icon,
  WaypointsIcon
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { JobStatusEnum, SortEnum } from '@/enums'
import {
  useDeleteProductPdfExport,
  useProductPdfExports,
  useBulkDeleteProductPdfExports,
  useCreateProductPdfExport
} from '@/hooks'
import { useFilesStore, useProductPdfExportsStore } from '@/store'
import { ProductPdfExportsFilterParams } from '@/types/product-pdf-export'
import {
  ConfirmationDialog,
  DeleteConfirmationDialog,
  JobStatusBadge,
  LoadingState,
  SortableHeader
} from '@/components/partials'
import { ProductPdfExportsFilters } from '.'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { BulkDeleteResponse } from '@/types/general'
import { formatDateTime, formatNumber, tOptional } from '@/lib/utils'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const [deleteProductPdfExportId, setDeleteProductPdfExportId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)
  const [regenerateConfirmProductPdfExport, setRegenerateConfirmProductPdfExport] = useState<any | null>(null)

  const { mutate: deleteProductPdfExportMutate, isPending: isDeleting } = useDeleteProductPdfExport()
  const { mutate: bulkDeleteProductPdfExportsMutate, isPending: isBulkDeleting } = useBulkDeleteProductPdfExports()
  const { mutate: createProductPdfExportMutate, isPending: isRegenerating } = useCreateProductPdfExport()
  const addToDownloadQueue = useFilesStore(state => state.addToDownloadQueue)

  const selectedProductPdfExportIds = useProductPdfExportsStore(state => state.selectedProductPdfExportIds)
  const toggleProductPdfExportSelection = useProductPdfExportsStore(state => state.toggleProductPdfExportSelection)
  const selectAllProductPdfExports = useProductPdfExportsStore(state => state.selectAllProductPdfExports)
  const deselectAllProductPdfExports = useProductPdfExportsStore(state => state.deselectAllProductPdfExports)

  const filters = useMemo<ProductPdfExportsFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())
    const statusesParam = params.get('statuses')
    const statuses = statusesParam ? statusesParam.split(',').filter(Boolean) : undefined

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.DESC,
      sortBy: params.get('sortBy') || 'createdAt',
      q: params.get('q') || '',
      locale: params.get('locale') || undefined,
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined,
      updatedAtStart: params.get('updatedAtStart') || undefined,
      updatedAtEnd: params.get('updatedAtEnd') || undefined,
      statuses
    }
  }, [searchParams])

  const { data: productPdfExportsData, isLoading, error, refetch, isFetching } = useProductPdfExports(filters)

  const updateUrlParams = (newFilters: Partial<ProductPdfExportsFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if ((key === 'createdUsers' || key === 'updatedUsers' || key === 'statuses') && Array.isArray(value)) {
          params.set(key, value.join(','))
        } else {
          params.set(key, value.toString())
        }
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<ProductPdfExportsFilterParams>) => updateUrlParams(newFilters)
  const handleResetFilters = () => router.push('/products/export/pdf')
  const handleSort = (sortBy: string, sort: SortEnum) => handleFiltersChange({ sortBy, sort, page: 1 })
  const handlePageChange = (page: number) => handleFiltersChange({ page })
  const handlePageSizeChange = (size: number) => handleFiltersChange({ size, page: 1 })
  const handleView = (id: string) => router.push(`/products/export/pdf/${id}`)
  const handleDelete = (id: string) => setDeleteProductPdfExportId(id)

  const handleRegenerate = (productPdfExport: any) => {
    setRegenerateConfirmProductPdfExport(productPdfExport)
  }

  const confirmRegenerate = () => {
    const target = regenerateConfirmProductPdfExport
    if (!target?.filters) {
      setRegenerateConfirmProductPdfExport(null)
      return
    }
    const regenerateParams = {
      ...target.filters,
      locale: target.locale,
      showPrices: target.showPrices,
      showCover: target.showCover,
      currencyCode: target.currencyCode,
      discountRate: target.discountRate
    }
    createProductPdfExportMutate(regenerateParams, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['productPdfExports'] })
        toast.success(t('created_successfully'))
        handleFiltersChange({ page: 1 })
        setRegenerateConfirmProductPdfExport(null)
      },
      onError: (error: any) => {
        console.error('Error regenerating productPdfExport:', error)
        toast.error(t('error_creating'))
        setRegenerateConfirmProductPdfExport(null)
      }
    })
  }

  const renderActionItems = (productPdfExport: any, isDropdown = true) => {
    const ItemComponent = isDropdown ? DropdownMenuItem : ContextMenuItem

    return (
      <>
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            handleView(productPdfExport.id)
          }}>
          <EyeIcon className="h-4 w-4" />
          {t('pdf_export_details')}
        </ItemComponent>
        {productPdfExport.status === JobStatusEnum.COMPLETED && productPdfExport.file && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              addToDownloadQueue(productPdfExport.file)
            }}>
            <DownloadIcon className="h-4 w-4" />
            {t('download')}
          </ItemComponent>
        )}
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            handleRegenerate(productPdfExport)
          }}
          disabled={isRegenerating}>
          <RefreshCwIcon className="h-4 w-4" />
          {t('regenerate')}
        </ItemComponent>
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            handleDelete(productPdfExport.id)
          }}
          className="text-destructive focus:text-destructive">
          <Trash2Icon className="h-4 w-4" />
          {t('delete')}
        </ItemComponent>
      </>
    )
  }

  const confirmDeleteMessage = () => {
    if (deleteProductPdfExportId) {
      deleteProductPdfExportMutate(deleteProductPdfExportId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['productPdfExports'] })
          toast.success(t('deleted_successfully'))
          setDeleteProductPdfExportId(null)
        },
        onError: (error: any) => {
          console.error('Error deleting productPdfExport:', error)
          toast.error(error.response?.data.message ?? t('error_deleting'))
          setDeleteProductPdfExportId(null)
        }
      })
    }
  }

  const handleBulkDelete = () => {
    if (selectedProductPdfExportIds.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  const confirmBulkDeleteProductPdfExports = () => {
    if (selectedProductPdfExportIds.length > 0) {
      bulkDeleteProductPdfExportsMutate(selectedProductPdfExportIds, {
        onSuccess: async (response: BulkDeleteResponse) => {
          await queryClient.invalidateQueries({ queryKey: ['productPdfExports'] })
          if (response.deletedItems && response.deletedItems.length > 0) {
            toast.success(t('deleted_successfully'))
          }
          setBulkDeleteConfirmOpen(false)
        },
        onError: (error: any) => {
          if (error.response?.status === 422) {
            toast.error(error.response.data?.items?.ids || t('error_deleting'))
          } else {
            toast.error(t('error_deleting'))
          }
          setBulkDeleteConfirmOpen(false)
        }
      })
    }
  }

  const handleSelectAll = () => {
    if (productPdfExportsData && productPdfExportsData.items.length > 0) {
      if (selectedProductPdfExportIds.length === productPdfExportsData.items.length) {
        deselectAllProductPdfExports()
      } else {
        selectAllProductPdfExports(productPdfExportsData.items)
      }
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ListIcon className="h-5 w-5" />
                {t('pdf_exports')}
              </CardTitle>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  const currentParams = searchParams.toString()
                  router.push(`/products${currentParams ? `?${currentParams}` : ''}`)
                }}>
                <PackageIcon />
                <span className="hidden items-center gap-2 lg:flex">{t('products')}</span>
              </Button>
              <Button onClick={() => router.push('/products/export/pdf/create')}>
                <SquarePlusIcon />
                <span className="hidden items-center gap-2 lg:flex">{t('create_pdf_export')}</span>
              </Button>
              {selectedProductPdfExportIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedProductPdfExportIds.length})</span>
                  </div>
                </Button>
              )}
              <ProductPdfExportsFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch} loadingRows={5}>
            {productPdfExportsData && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          <div className="flex items-center justify-center">
                            <Checkbox
                              checked={
                                productPdfExportsData &&
                                productPdfExportsData.items.length > 0 &&
                                selectedProductPdfExportIds.length === productPdfExportsData.items.length
                              }
                              onCheckedChange={handleSelectAll}
                              onClick={e => e.stopPropagation()}
                              className="cursor-pointer"
                            />
                          </div>
                        </TableHead>
                        <TableHead className="min-w-50">
                          <SortableHeader
                            sortBy="id"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <HashIcon className="h-4 w-4" />
                            ID
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-40 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <GlobeIcon className="h-4 w-4" />
                            {t('language')}
                          </div>
                        </TableHead>
                        <TableHead className="min-w-50 text-right">
                          <SortableHeader
                            sortBy="total"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalculatorIcon className="h-4 w-4" />
                            {t('total')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="status"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <WaypointsIcon className="h-4 w-4" />
                            {t('status')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 w-4" />
                            {t('created_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="updatedAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 w-4" />
                            {t('updated_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-25 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {productPdfExportsData.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        productPdfExportsData.items.map((productPdfExport, index) => (
                          <ContextMenu key={productPdfExport.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                                  index % 2 === 0 ? 'bg-background' : 'bg-muted/25'
                                }`}
                                onClick={() => handleView(productPdfExport.id)}>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center">
                                    <Checkbox
                                      checked={selectedProductPdfExportIds.includes(productPdfExport.id)}
                                      onCheckedChange={() => toggleProductPdfExportSelection(productPdfExport.id)}
                                      onClick={e => e.stopPropagation()}
                                      className="cursor-pointer"
                                    />
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <span className="text-muted-foreground text-xs">{productPdfExport.id}</span>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge variant="outline" className="font-mono text-xs">
                                    {productPdfExport.locale}
                                  </Badge>
                                </TableCell>
                                <TableCell className="pr-5 text-right">
                                  {formatNumber(productPdfExport.total, locale)}
                                </TableCell>
                                <TableCell className="text-center">
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <div>
                                        <JobStatusBadge status={productPdfExport.status} />
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      {productPdfExport.message ? tOptional(t, productPdfExport.message) : ''}
                                    </TooltipContent>
                                  </Tooltip>
                                </TableCell>
                                <TableCell className="text-center">
                                  <span className="text-muted-foreground text-sm">
                                    {formatDateTime(productPdfExport.createdAt, locale)}
                                  </span>
                                </TableCell>
                                <TableCell className="text-center">
                                  <span className="text-muted-foreground text-sm">
                                    {formatDateTime(productPdfExport.updatedAt, locale)}
                                  </span>
                                </TableCell>
                                <TableCell className="text-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={e => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }}>
                                        <span className="sr-only">{t('open_menu')}</span>
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {renderActionItems(productPdfExport, true)}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>{renderActionItems(productPdfExport, false)}</ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <DataTablePagination
                  currentPage={productPdfExportsData.page}
                  totalPages={productPdfExportsData.pages}
                  totalItems={productPdfExportsData.total}
                  pageSize={productPdfExportsData.size}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      <ConfirmationDialog
        open={regenerateConfirmProductPdfExport !== null}
        onCancel={() => setRegenerateConfirmProductPdfExport(null)}
        onConfirm={confirmRegenerate}
        description={t('regenerate_export_confirmation')}
        confirmText={t('regenerate')}
        isLoading={isRegenerating}
      />

      <DeleteConfirmationDialog
        open={deleteProductPdfExportId !== null}
        onCancel={() => setDeleteProductPdfExportId(null)}
        onConfirm={confirmDeleteMessage}
        isDeleting={isDeleting}
      />

      <DeleteConfirmationDialog
        open={bulkDeleteConfirmOpen}
        onCancel={() => setBulkDeleteConfirmOpen(false)}
        onConfirm={confirmBulkDeleteProductPdfExports}
        isDeleting={isBulkDeleting}
      />
    </>
  )
}

export default List
